/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Record type to create. `ALIAS` is not a real DNS record type: it means an apex-compatible
 *
 * @remarks
 * alias, which providers expose as ALIAS, ANAME, or a flattened CNAME. Apex domains cannot
 * hold a plain CNAME, so they receive `ALIAS` where a subdomain receives `CNAME`.
 */
export const Type = {
  Cname: "CNAME",
  Alias: "ALIAS",
  Txt: "TXT",
} as const;
/**
 * Record type to create. `ALIAS` is not a real DNS record type: it means an apex-compatible
 *
 * @remarks
 * alias, which providers expose as ALIAS, ANAME, or a flattened CNAME. Apex domains cannot
 * hold a plain CNAME, so they receive `ALIAS` where a subdomain receives `CNAME`.
 */
export type Type = ClosedEnum<typeof Type>;

export type DnsRecord = {
  /**
   * Record type to create. `ALIAS` is not a real DNS record type: it means an apex-compatible
   *
   * @remarks
   * alias, which providers expose as ALIAS, ANAME, or a flattened CNAME. Apex domains cannot
   * hold a plain CNAME, so they receive `ALIAS` where a subdomain receives `CNAME`.
   */
  type: Type;
  /**
   * Fully qualified name of the record, ready to use as-is.
   *
   * @remarks
   *
   * Some providers want a name relative to the zone instead. Drop the zone and its trailing dot:
   * in zone `acme.com`, `api.acme.com` becomes `api` and `_teral.api.acme.com` becomes
   * `_teral.api`. A name equal to the zone itself is usually entered as `@`.
   */
  name: string;
  /**
   * The value to set on the record, exactly as given, including any prefix.
   *
   * @remarks
   * Do not trim or reformat it: verification compares the published record against this string.
   *
   * Use the lowest TTL your provider allows until the domain is verified. Verification polls DNS,
   * so a long TTL keeps a stale value cached and can burn the verification window on a value you
   * have already corrected. Raise it afterwards if you want.
   */
  value: string;
  /**
   * Seconds a resolver may cache this record. Set it in your provider alongside the record's
   *
   * @remarks
   * name and value.
   */
  ttl: number;
  /**
   * Whether Teral has read this record back with the expected value. Use it to see which
   *
   * @remarks
   * records are still outstanding.
   *
   * False does not always mean the record is missing. A provider that does not expose the
   * published value to a DNS lookup, such as a proxied or flattened routing record, leaves
   * this false for as long as it serves traffic; such a domain verifies through its TXT
   * record instead. Always false on a domain no check has run against yet.
   */
  verified: boolean;
  /**
   * What this record is for and any provider-specific caveat that applies to it.
   *
   * @remarks
   * Worth surfacing to whoever edits the DNS zone. Treat it as optional: it carries
   * no data the record itself needs, so a future record type may omit it.
   */
  note?: string | undefined;
};

/** @internal */
export const Type$inboundSchema: z.ZodNativeEnum<typeof Type> = z.nativeEnum(
  Type,
);

/** @internal */
export const DnsRecord$inboundSchema: z.ZodType<
  DnsRecord,
  z.ZodTypeDef,
  unknown
> = z.object({
  type: Type$inboundSchema,
  name: z.string(),
  value: z.string(),
  ttl: z.number().int(),
  verified: z.boolean(),
  note: z.string().optional(),
});

export function dnsRecordFromJSON(
  jsonString: string,
): SafeParseResult<DnsRecord, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DnsRecord$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DnsRecord' from JSON`,
  );
}
